import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Alert,
  CircularProgress,
  useTheme,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  TextField,
  IconButton,
  Tooltip,
  alpha,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  Divider,
  Grid,
} from '@mui/material';
import { AreaChart, Area, XAxis, YAxis, ResponsiveContainer, Tooltip as RechartsTooltip, CartesianGrid } from 'recharts';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import DatePicker from '../shared/components/forms/HotelDatePicker';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import api from '../services/api';
import useMobileView from '../hooks/useMobileView';
import PaletteIcon from '@mui/icons-material/Palette';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import LightModeIcon from '@mui/icons-material/LightMode';
import NightlightRoundIcon from '@mui/icons-material/NightlightRound';
import RefreshIcon from '@mui/icons-material/Refresh';
import LaptopIcon from '@mui/icons-material/Laptop';
import SmartphoneIcon from '@mui/icons-material/Smartphone';
import ImageIcon from '@mui/icons-material/Image';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import PeopleIcon from '@mui/icons-material/People';
import PublicIcon from '@mui/icons-material/Public';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import PaymentsIcon from '@mui/icons-material/Payments';
import HotelOutlinedIcon from '@mui/icons-material/HotelOutlined';
import PieChartOutlineIcon from '@mui/icons-material/PieChartOutline';
import CreditCardOutlinedIcon from '@mui/icons-material/CreditCardOutlined';

const THEME_COLOR = '#4ce1b6';
const DEFAULT_COLOR = '#4ce1b6';

const NEXASTAY_THEME_PRESET_KEY = 'nexastay_theme_preset';
const THEME_PRESET_KEYS = ['dark', 'light', 'midnight'];
const THEME_PRESETS = [
  { key: 'dark', label: 'Dark', Icon: DarkModeIcon, color: '#4ce1b6', desc: 'Dark background' },
  { key: 'light', label: 'Light', Icon: LightModeIcon, color: '#0d9488', desc: 'Light background' },
  { key: 'midnight', label: 'Midnight', Icon: NightlightRoundIcon, color: '#38bdf8', desc: 'Slate blue' },
];
const PRESET_COLORS = [
  '#4ce1b6',
  '#2196f3',
  '#9c27b0',
  '#f44336',
  '#ff9800',
  '#4caf50',
  '#00bcd4',
  '#e91e63',
  '#0d9488',
  '#6366f1',
];

function getPreviewHotelId() {
  if (typeof window === 'undefined') return '2';
  const id = window.DJANGO_CONTEXT?.selectedHotel?.id;
  return id != null ? String(id) : '2';
}

function PaymentOptionTile({
  icon: Icon,
  title,
  subtitle,
  enabled,
  onToggle,
  disabled,
  accentColor,
  isDark,
  textColor,
  secondaryColor,
  borderColor,
  badge,
  footer,
}) {
  return (
    <Paper
      elevation={0}
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        gap: 0.75,
        p: 1,
        borderRadius: 1.5,
        border: enabled ? `1.5px solid ${accentColor}` : `1px solid ${borderColor}`,
        bgcolor: enabled ? alpha(accentColor, isDark ? 0.12 : 0.08) : isDark ? alpha('#fff', 0.03) : alpha('#000', 0.02),
        transition: 'border-color 0.2s, background 0.2s',
        opacity: disabled ? 0.72 : 1,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
        <Box
          sx={{
            width: 28,
            height: 28,
            borderRadius: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
            bgcolor: enabled ? alpha(accentColor, 0.2) : isDark ? alpha('#fff', 0.06) : alpha('#000', 0.05),
            color: enabled ? accentColor : secondaryColor,
          }}
        >
          <Icon sx={{ fontSize: 16 }} />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
            <Typography variant="caption" sx={{ color: textColor, fontWeight: 700, lineHeight: 1.2 }}>
              {title}
            </Typography>
            {enabled && badge ? (
              <Chip
                size="small"
                label={badge}
                sx={{
                  height: 18,
                  fontSize: '0.6rem',
                  fontWeight: 700,
                  bgcolor: alpha(accentColor, 0.14),
                  color: accentColor,
                  '& .MuiChip-label': { px: 0.75 },
                }}
              />
            ) : null}
          </Box>
          {subtitle ? (
            <Typography variant="caption" sx={{ color: secondaryColor, display: 'block', lineHeight: 1.25, fontSize: '0.68rem' }}>
              {subtitle}
            </Typography>
          ) : null}
        </Box>
        <Switch
          checked={enabled}
          disabled={disabled}
          onChange={(e) => onToggle(e.target.checked)}
          size="small"
          sx={{
            flexShrink: 0,
            transform: 'scale(0.85)',
            transformOrigin: 'center right',
            '& .MuiSwitch-switchBase.Mui-checked': { color: accentColor },
            '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: accentColor },
          }}
        />
      </Box>
      {footer ? footer : null}
    </Paper>
  );
}

function buildPreviewIframeSrc(previewUrl, previewRefreshKey) {
  if (!previewRefreshKey) return previewUrl;
  return `${previewUrl}${previewUrl.includes('?') ? '&' : '?'}__preview=${previewRefreshKey}`;
}

function NexaStayMobilePreview({
  previewUrl,
  previewRefreshKey,
  isDark,
  borderColor,
  secondaryColor,
  isCompact,
}) {
  const iframeSrc = buildPreviewIframeSrc(previewUrl, previewRefreshKey);

  if (isCompact) {
    return (
      <Box sx={{ width: '100%' }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1,
            mb: 1.25,
          }}
        >
          <Typography
            variant="caption"
            sx={{ color: secondaryColor, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 0.5 }}
          >
            <SmartphoneIcon sx={{ fontSize: 16 }} />
            Mobile preview
          </Typography>
          <Button
            component="a"
            href={previewUrl}
            target="_blank"
            rel="noopener noreferrer"
            size="small"
            startIcon={<OpenInNewIcon sx={{ fontSize: 14 }} />}
            sx={{ color: secondaryColor, fontSize: '0.72rem', minWidth: 0, px: 1, py: 0.25 }}
          >
            Open site
          </Button>
        </Box>
        <Paper
          elevation={0}
          sx={{
            width: '100%',
            borderRadius: 3,
            overflow: 'hidden',
            border: `1px solid ${borderColor}`,
            bgcolor: isDark ? '#12151c' : '#fff',
            display: 'flex',
            flexDirection: 'column',
            height: { xs: 'min(72vh, 640px)', sm: 560 },
            minHeight: 420,
            boxShadow: isDark ? '0 12px 32px rgba(0,0,0,0.35)' : '0 8px 24px rgba(0,0,0,0.08)',
          }}
        >
          <Box
            sx={{
              height: 28,
              flexShrink: 0,
              bgcolor: isDark ? alpha('#fff', 0.06) : alpha('#000', 0.04),
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Box
              sx={{
                width: 52,
                height: 4,
                borderRadius: 999,
                bgcolor: isDark ? alpha('#fff', 0.14) : alpha('#000', 0.12),
              }}
            />
          </Box>
          <Box sx={{ flex: 1, minHeight: 0, position: 'relative', bgcolor: '#fff' }}>
            <Box
              component="iframe"
              title="NexaStay mobile preview"
              src={iframeSrc}
              sx={{ position: 'absolute', inset: 0, width: '100%', height: '100%', border: 0 }}
            />
          </Box>
        </Paper>
      </Box>
    );
  }

  const phoneOuterWidth = 280;
  const phoneScreenWidth = phoneOuterWidth - 16;
  const phoneScreenHeight = 504;

  return (
    <Box
      sx={{
        width: phoneOuterWidth,
        flexShrink: 0,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 1.5,
      }}
    >
      <Typography
        variant="caption"
        sx={{ color: secondaryColor, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 0.5 }}
      >
        <SmartphoneIcon sx={{ fontSize: 16 }} />
        Mobile view
      </Typography>
      <Box
        sx={{
          width: phoneOuterWidth,
          padding: '10px 8px 12px',
          borderRadius: 4,
          border: `3px solid ${isDark ? '#2a2a2a' : '#1a1a1a'}`,
          backgroundColor: isDark ? '#1a1a1a' : '#2a2a2a',
          boxShadow: `inset 0 0 0 1px ${alpha('#fff', 0.04)}`,
        }}
      >
        <Paper
          elevation={0}
          sx={{
            width: phoneScreenWidth,
            height: phoneScreenHeight,
            display: 'flex',
            flexDirection: 'column',
            borderRadius: 2,
            overflow: 'hidden',
            border: `1px solid ${borderColor}`,
            backgroundColor: isDark ? '#12151c' : '#fff',
          }}
        >
          <Box
            sx={{
              height: 24,
              flexShrink: 0,
              backgroundColor: isDark ? alpha('#fff', 0.08) : alpha('#000', 0.06),
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Box sx={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: secondaryColor, opacity: 0.6 }} />
          </Box>
          <Box sx={{ flex: 1, minHeight: 0, position: 'relative', backgroundColor: '#fff' }}>
            <Box
              component="iframe"
              title="NexaStay mobile preview"
              src={iframeSrc}
              sx={{ position: 'absolute', inset: 0, width: '100%', height: '100%', border: 0 }}
            />
          </Box>
        </Paper>
      </Box>
    </Box>
  );
}

const NexaStaySection = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const { isCompact } = useMobileView();
  const iframeRef = useRef(null);

  const [themeColor, setThemeColor] = useState(DEFAULT_COLOR);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [customColor, setCustomColor] = useState('');
  const [themePreset, setThemePreset] = useState(() => {
    if (typeof window === 'undefined') return 'dark';
    const stored = window.localStorage.getItem(NEXASTAY_THEME_PRESET_KEY) || 'dark';
    return THEME_PRESET_KEYS.includes(stored) ? stored : (stored === 'gradient' ? 'light' : 'dark');
  });
  const [presetSuccess, setPresetSuccess] = useState(false);
  const [nexastaySlug, setNexastaySlug] = useState('');
  const [slugInput, setSlugInput] = useState('');
  const [savingSlug, setSavingSlug] = useState(false);
  const [slugSuccess, setSlugSuccess] = useState(false);
  const [previewRefreshKey, setPreviewRefreshKey] = useState(0);
  const [logoUrl, setLogoUrl] = useState(null);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const logoInputRef = useRef(null);
  const [analyticsPeriod, setAnalyticsPeriod] = useState('this_month');
  const [customDateStart, setCustomDateStart] = useState(null);
  const [customDateEnd, setCustomDateEnd] = useState(null);
  const [analyticsData, setAnalyticsData] = useState(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [analyticsError, setAnalyticsError] = useState(null);
  const [payAtHotelEnabled, setPayAtHotelEnabled] = useState(true);
  const [partialPayEnabled, setPartialPayEnabled] = useState(false);
  const [fullPayEnabled, setFullPayEnabled] = useState(false);
  const [partialPayPercent, setPartialPayPercent] = useState(30);
  const [razorpayConfigured, setRazorpayConfigured] = useState(false);
  const [savingPayment, setSavingPayment] = useState(false);
  const [paymentSuccess, setPaymentSuccess] = useState(false);
  const [paymentError, setPaymentError] = useState(null);

  const previewHotelId = getPreviewHotelId();
  const baseHotelPath = '/nexastay/hotel/';
  const hotelPathSegment = nexastaySlug || previewHotelId;
  const baseOrigin = typeof window !== 'undefined' ? window.location.origin : '';
  const isLocal = typeof window !== 'undefined' && (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');
  // Local: preview uses current origin + /nexastay/hotel/... (Django serves that path).
  // Prod: slug CNAME uses https://<slug>.roomnexa.com.
  const NEXASTAY_ROOT_DOMAIN = 'roomnexa.com';
  const fullHotelUrl = isLocal
    ? (baseOrigin ? `${baseOrigin}${baseHotelPath}${hotelPathSegment}` : `${baseHotelPath}${hotelPathSegment}`)
    : (nexastaySlug
      ? `https://${nexastaySlug}.${NEXASTAY_ROOT_DOMAIN}`
      : (baseOrigin ? `${baseOrigin}${baseHotelPath}${previewHotelId}` : `${baseHotelPath}${previewHotelId}`));
  const previewUrl = fullHotelUrl;

  // Fetch visitor analytics from backend (unique visitors = distinct IP in period)
  useEffect(() => {
    let cancelled = false;
    if (analyticsPeriod === 'custom' && (!customDateStart || !customDateEnd)) {
      setAnalyticsData(null);
      return;
    }
    setAnalyticsLoading(true);
    setAnalyticsError(null);
    const params = new URLSearchParams({ period: analyticsPeriod });
    if (analyticsPeriod === 'custom' && customDateStart && customDateEnd) {
      const startStr = customDateStart.format ? customDateStart.format('YYYY-MM-DD') : customDateStart;
      const endStr = customDateEnd.format ? customDateEnd.format('YYYY-MM-DD') : customDateEnd;
      params.set('start_date', startStr);
      params.set('end_date', endStr);
    }
    api
      .get(`/distribution/api/nexastay-visitors/?${params.toString()}`)
      .then(({ data }) => {
        if (!cancelled) {
          setAnalyticsData({
            totalVisitors: data.total_unique_visitors ?? 0,
            chartData: data.chart_data ?? [],
            byCountry: data.by_country ?? [],
          });
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setAnalyticsError(err.response?.data?.error || 'Failed to load analytics');
          setAnalyticsData((prev) => prev || { totalVisitors: 0, chartData: [], byCountry: [] });
        }
      })
      .finally(() => {
        if (!cancelled) setAnalyticsLoading(false);
      });
    return () => { cancelled = true; };
  }, [analyticsPeriod, customDateStart, customDateEnd]);

  const analytics = useMemo(() => {
    if (analyticsData) {
      return analyticsData;
    }
    return {
      totalVisitors: 0,
      chartData: [],
      byCountry: [],
    };
  }, [analyticsData]);

  const refreshPreview = useCallback(() => {
    setPreviewRefreshKey((k) => k + 1);
  }, []);

  useEffect(() => {
    fetchTheme();
  }, []);

  const fetchTheme = async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await api.get('/distribution/api/nexastay-theme/');
      const color = (data.theme_color || DEFAULT_COLOR).trim();
      setThemeColor(color.startsWith('#') ? color : '#' + color);
      setCustomColor(color.startsWith('#') ? color : '#' + color);
      const slug = (data.nexastay_slug || '').trim() || '';
      setNexastaySlug(slug);
      setSlugInput(slug);
      const preset = (data.nexastay_theme_preset || '').trim().toLowerCase();
      if (THEME_PRESET_KEYS.includes(preset)) setThemePreset(preset);
      setLogoUrl(data.logo_url || null);
      setPayAtHotelEnabled(data.pay_at_hotel_enabled !== false);
      setPartialPayEnabled(Boolean(data.partial_pay_enabled));
      setFullPayEnabled(Boolean(data.full_pay_enabled));
      setPartialPayPercent(Number(data.partial_pay_percent) || 30);
      setRazorpayConfigured(Boolean(data.razorpay_configured));
    } catch (e) {
      const msg = e.response?.data?.error || e.response?.data?.detail || (e.response?.data && typeof e.response.data === 'string' ? e.response.data : null);
      setError(msg || 'Failed to load theme color.');
      setThemeColor(DEFAULT_COLOR);
      setCustomColor(DEFAULT_COLOR);
    } finally {
      setLoading(false);
    }
  };

  const saveTheme = async (newColor) => {
    const hex = (newColor || themeColor).trim();
    const normalized = hex.startsWith('#') ? hex : '#' + hex;
    setSaving(true);
    setError(null);
    setSuccess(false);
    try {
      const { data } = await api.patch('/distribution/api/nexastay-theme/', { theme_color: normalized });
      const saved = (data.theme_color || DEFAULT_COLOR).trim();
      setThemeColor(saved.startsWith('#') ? saved : '#' + saved);
      setCustomColor(saved.startsWith('#') ? saved : '#' + saved);
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
      refreshPreview();
    } catch (e) {
      const msg = e.response?.data?.error || e.response?.data?.detail;
      setError(msg || 'Failed to save theme color.');
    } finally {
      setSaving(false);
    }
  };

  const handlePresetClick = (color) => {
    setThemeColor(color);
    setCustomColor(color);
    saveTheme(color);
  };

  const handleCustomChange = (e) => {
    const v = e.target.value;
    setCustomColor(v);
    if (/^#[0-9A-Fa-f]{3}$|^#[0-9A-Fa-f]{6}$/.test(v)) {
      setThemeColor(v);
      saveTheme(v);
    }
  };

  const handleThemePresetChange = async (key) => {
    if (typeof window === 'undefined') return;
    window.localStorage.setItem(NEXASTAY_THEME_PRESET_KEY, key);
    setThemePreset(key);
    setPresetSuccess(true);
    setTimeout(() => setPresetSuccess(false), 2500);
    try {
      await api.patch('/distribution/api/nexastay-theme/', { nexastay_theme_preset: key });
      refreshPreview();
    } catch (e) {
      const msg = e.response?.data?.error || e.response?.data?.detail;
      setError(msg || 'Failed to save theme style.');
    }
  };

  const savePaymentSettings = async (overrides = {}) => {
    const nextPayAt = overrides.payAtHotelEnabled ?? payAtHotelEnabled;
    const nextPartial = overrides.partialPayEnabled ?? partialPayEnabled;
    const nextFull = overrides.fullPayEnabled ?? fullPayEnabled;
    const nextPercent = overrides.partialPayPercent ?? partialPayPercent;
    if (!nextPayAt && !nextPartial && !nextFull) {
      setPaymentError('At least one payment option must be enabled.');
      return;
    }
    setSavingPayment(true);
    setPaymentError(null);
    setPaymentSuccess(false);
    try {
      const { data } = await api.patch('/distribution/api/nexastay-theme/', {
        pay_at_hotel_enabled: nextPayAt,
        partial_pay_enabled: nextPartial,
        full_pay_enabled: nextFull,
        partial_pay_percent: Number(nextPercent) || 30,
      });
      setPayAtHotelEnabled(data.pay_at_hotel_enabled !== false);
      setPartialPayEnabled(Boolean(data.partial_pay_enabled));
      setFullPayEnabled(Boolean(data.full_pay_enabled));
      setPartialPayPercent(Number(data.partial_pay_percent) || 30);
      setRazorpayConfigured(Boolean(data.razorpay_configured));
      setPaymentSuccess(true);
      setTimeout(() => setPaymentSuccess(false), 3000);
      refreshPreview();
    } catch (err) {
      const msg = err.response?.data?.error || err.response?.data?.detail;
      setPaymentError(msg || 'Failed to save payment settings.');
    } finally {
      setSavingPayment(false);
    }
  };

  const handleLogoUpload = async (e) => {
    const file = e?.target?.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      setError('Please choose an image file (JPEG, PNG, GIF, WebP).');
      return;
    }
    setUploadingLogo(true);
    setError(null);
    try {
      const formData = new FormData();
      formData.append('logo', file);
      const { data } = await api.post('/distribution/api/nexastay-theme/logo/', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setLogoUrl(data.logo_url || null);
      refreshPreview();
    } catch (err) {
      const msg = err.response?.data?.error || err.response?.data?.detail || 'Failed to upload logo.';
      setError(typeof msg === 'string' ? msg : 'Failed to upload logo.');
    } finally {
      setUploadingLogo(false);
      if (logoInputRef.current) logoInputRef.current.value = '';
    }
  };

  const saveSlug = async () => {
    const raw = (slugInput || '').trim();
    const slug = raw ? raw.toLowerCase().replace(/[^a-z0-9-]/g, '-').replace(/-+/g, '-').replace(/^-|-$/g, '') : '';
    setSavingSlug(true);
    setError(null);
    setSlugSuccess(false);
    try {
      const { data } = await api.patch('/distribution/api/nexastay-theme/', { nexastay_slug: slug || null });
      const saved = (data.nexastay_slug || '').trim() || '';
      setNexastaySlug(saved);
      setSlugInput(saved);
      setSlugSuccess(true);
      setTimeout(() => setSlugSuccess(false), 3000);
      refreshPreview();
    } catch (e) {
      const msg = e.response?.data?.error || e.response?.data?.detail;
      setError(msg || 'Failed to save hotel URL.');
    } finally {
      setSavingSlug(false);
    }
  };

  const textColor = isDark ? '#ffffff' : '#212121';
  const secondaryColor = isDark ? 'rgba(255,255,255,0.85)' : 'rgba(0,0,0,0.6)';
  const cardBg = isDark ? alpha('#fff', 0.06) : alpha('#000', 0.02);
  const borderColor = isDark ? alpha('#fff', 0.12) : alpha('#000', 0.08);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 360 }}>
        <CircularProgress sx={{ color: THEME_COLOR }} />
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, height: '100%' }}>
      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ borderRadius: 2 }}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ borderRadius: 2 }}>
          Accent color saved. Preview updated.
        </Alert>
      )}
      {slugSuccess && (
        <Alert severity="success" sx={{ borderRadius: 2 }}>
          Hotel URL saved. Use this link to share your booking page.
        </Alert>
      )}
      {paymentSuccess && (
        <Alert severity="success" sx={{ borderRadius: 2 }}>
          Payment settings saved.
        </Alert>
      )}

      {/* Booking engine settings: accent color, hotel URL, payment gateway */}
      <Paper
        elevation={0}
        sx={{
          borderRadius: 2,
          overflow: 'hidden',
          border: `1px solid ${alpha(themeColor, isDark ? 0.3 : 0.22)}`,
          bgcolor: cardBg,
        }}
      >
        <Box
          sx={{
            px: 1.5,
            py: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1,
            flexWrap: 'wrap',
            borderBottom: `1px solid ${alpha(themeColor, 0.14)}`,
            bgcolor: alpha(themeColor, isDark ? 0.08 : 0.06),
          }}
        >
          <Typography variant="subtitle2" sx={{ color: textColor, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <PaletteIcon sx={{ fontSize: 18, color: themeColor }} />
            Booking engine settings
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <Chip
              size="small"
              label={themeColor.toUpperCase()}
              sx={{
                height: 22,
                fontFamily: 'monospace',
                fontSize: '0.65rem',
                bgcolor: alpha(themeColor, 0.14),
                color: textColor,
              }}
            />
            {(saving || savingPayment || savingSlug) && (
              <CircularProgress size={14} sx={{ color: themeColor }} />
            )}
          </Box>
        </Box>

        <Box sx={{ p: 1.5, display: 'flex', flexDirection: 'column', gap: 1.25 }}>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', lg: '1fr auto 1fr' },
              gap: 1.25,
              alignItems: 'center',
            }}
          >
            {/* Accent color */}
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="caption" sx={{ color: secondaryColor, fontWeight: 600, display: 'block', mb: 0.75 }}>
                Accent
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                {PRESET_COLORS.map((color) => (
                  <Tooltip key={color} title={color}>
                    <Box
                      onClick={() => handlePresetClick(color)}
                      sx={{
                        width: 26,
                        height: 26,
                        borderRadius: 1,
                        bgcolor: color,
                        border: themeColor === color ? `2px solid ${themeColor}` : color === '#ffffff' ? `1px solid ${borderColor}` : '1px solid transparent',
                        cursor: 'pointer',
                        '&:hover': { transform: 'scale(1.06)' },
                      }}
                    />
                  </Tooltip>
                ))}
                <input
                  type="color"
                  value={themeColor}
                  onChange={(e) => {
                    const v = e.target.value;
                    setThemeColor(v);
                    setCustomColor(v);
                    saveTheme(v);
                  }}
                  style={{ width: 26, height: 26, border: 'none', borderRadius: 6, cursor: 'pointer', padding: 0 }}
                />
                <TextField
                  size="small"
                  value={customColor}
                  onChange={handleCustomChange}
                  placeholder="#4ce1b6"
                  sx={{
                    width: 92,
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 1.5,
                      fontSize: '0.75rem',
                      height: 32,
                      bgcolor: isDark ? alpha('#fff', 0.06) : alpha('#fff', 0.8),
                    },
                  }}
                  inputProps={{ maxLength: 7 }}
                />
                <Button
                  variant="contained"
                  size="small"
                  disabled={saving}
                  onClick={() => saveTheme(customColor)}
                  sx={{
                    minWidth: 0,
                    px: 1.25,
                    py: 0.25,
                    fontSize: '0.75rem',
                    borderRadius: 1.5,
                    bgcolor: themeColor,
                    color: '#121212',
                    '&:hover': { bgcolor: alpha(themeColor, 0.9) },
                  }}
                >
                  Apply
                </Button>
              </Box>
            </Box>

            <Divider orientation="vertical" flexItem sx={{ display: { xs: 'none', lg: 'block' }, borderColor: alpha(themeColor, 0.12) }} />

            {/* Hotel URL */}
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="caption" sx={{ color: secondaryColor, fontWeight: 600, display: 'block', mb: 0.75 }}>
                Hotel URL
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                <Chip
                  label={fullHotelUrl}
                  size="small"
                  sx={{
                    maxWidth: { xs: '100%', md: 280 },
                    height: 28,
                    fontFamily: 'monospace',
                    fontSize: '0.68rem',
                    bgcolor: isDark ? alpha('#fff', 0.06) : alpha('#fff', 0.8),
                    '& .MuiChip-label': { overflow: 'hidden', textOverflow: 'ellipsis' },
                  }}
                />
                <TextField
                  size="small"
                  placeholder="slug"
                  value={slugInput}
                  onChange={(e) => setSlugInput(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && saveSlug()}
                  sx={{
                    width: 110,
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 1.5,
                      fontSize: '0.75rem',
                      height: 32,
                      bgcolor: isDark ? alpha('#fff', 0.06) : alpha('#fff', 0.8),
                    },
                  }}
                  inputProps={{ maxLength: 80 }}
                />
                <Button
                  variant="outlined"
                  size="small"
                  disabled={savingSlug}
                  onClick={saveSlug}
                  sx={{
                    minWidth: 0,
                    px: 1.25,
                    py: 0.25,
                    fontSize: '0.75rem',
                    borderRadius: 1.5,
                    borderColor: alpha(themeColor, 0.45),
                    color: themeColor,
                  }}
                >
                  Set
                </Button>
              </Box>
            </Box>
          </Box>

          <Divider sx={{ borderColor: alpha(themeColor, 0.1) }} />

          {/* Payment gateway */}
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.75 }}>
              <PaymentsIcon sx={{ color: themeColor, fontSize: 16 }} />
              <Typography variant="caption" sx={{ color: secondaryColor, fontWeight: 600 }}>
                Payment gateway
              </Typography>
            </Box>
            {!razorpayConfigured && (partialPayEnabled || fullPayEnabled) && (
              <Alert severity="warning" sx={{ py: 0, mb: 0.75, borderRadius: 1.5, fontSize: '0.75rem' }} icon={false}>
                Razorpay not configured — online options hidden until keys are added.
              </Alert>
            )}
            {paymentError && (
              <Alert severity="error" sx={{ py: 0, mb: 0.75, borderRadius: 1.5, fontSize: '0.75rem' }} onClose={() => setPaymentError(null)}>
                {paymentError}
              </Alert>
            )}
            <Grid container spacing={1}>
              <Grid item xs={12} md={4}>
                <PaymentOptionTile
                  icon={HotelOutlinedIcon}
                  title="Pay at hotel"
                  subtitle="Pay on check-in"
                  enabled={payAtHotelEnabled}
                  disabled={savingPayment}
                  accentColor={themeColor}
                  isDark={isDark}
                  textColor={textColor}
                  secondaryColor={secondaryColor}
                  borderColor={alpha(themeColor, 0.18)}
                  badge="Live"
                  onToggle={(checked) => {
                    setPayAtHotelEnabled(checked);
                    savePaymentSettings({ payAtHotelEnabled: checked });
                  }}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <PaymentOptionTile
                  icon={PieChartOutlineIcon}
                  title="Partial pay"
                  subtitle="Deposit via Razorpay"
                  enabled={partialPayEnabled}
                  disabled={savingPayment}
                  accentColor="#6366f1"
                  isDark={isDark}
                  textColor={textColor}
                  secondaryColor={secondaryColor}
                  borderColor={alpha(themeColor, 0.18)}
                  badge="Razorpay"
                  onToggle={(checked) => {
                    setPartialPayEnabled(checked);
                    savePaymentSettings({ partialPayEnabled: checked });
                  }}
                  footer={
                    partialPayEnabled ? (
                      <TextField
                        type="number"
                        size="small"
                        label="%"
                        value={partialPayPercent}
                        disabled={savingPayment}
                        inputProps={{ min: 1, max: 99 }}
                        onChange={(e) => setPartialPayPercent(Number(e.target.value) || 30)}
                        onBlur={() => savePaymentSettings({ partialPayPercent })}
                        sx={{
                          '& .MuiOutlinedInput-root': { borderRadius: 1.5, height: 30, fontSize: '0.75rem' },
                          '& .MuiInputLabel-root': { fontSize: '0.75rem', top: -2 },
                        }}
                      />
                    ) : null
                  }
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <PaymentOptionTile
                  icon={CreditCardOutlinedIcon}
                  title="Full payment"
                  subtitle="100% via Razorpay"
                  enabled={fullPayEnabled}
                  disabled={savingPayment}
                  accentColor="#0ea5e9"
                  isDark={isDark}
                  textColor={textColor}
                  secondaryColor={secondaryColor}
                  borderColor={alpha(themeColor, 0.18)}
                  badge="Razorpay"
                  onToggle={(checked) => {
                    setFullPayEnabled(checked);
                    savePaymentSettings({ fullPayEnabled: checked });
                  }}
                />
              </Grid>
            </Grid>
          </Box>
        </Box>
      </Paper>

      {/* Row 1: Theme style + mobile preview + analytics */}
      <Box
        sx={{
          display: 'flex',
          gap: 2,
          flexDirection: { xs: 'column', lg: 'row' },
          flexWrap: 'nowrap',
          alignItems: { xs: 'stretch', lg: 'flex-start' },
        }}
      >
        {/* Left: Theme style list */}
        <Paper
          elevation={0}
          sx={{
            width: { xs: '100%', lg: 280 },
            flexShrink: 0,
            borderRadius: 3,
            backgroundColor: cardBg,
            border: `1px solid ${borderColor}`,
            overflow: 'hidden',
          }}
        >
          <Box sx={{ p: 2, borderBottom: `1px solid ${borderColor}` }}>
            <Typography variant="subtitle1" sx={{ color: textColor, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1 }}>
              <PaletteIcon sx={{ color: THEME_COLOR, fontSize: 22 }} />
              Theme style
            </Typography>
            <Typography variant="caption" sx={{ color: secondaryColor, display: 'block', mt: 0.5 }}>
              Background &amp; look of your booking page
            </Typography>
          </Box>
          <List disablePadding sx={{ py: 1 }}>
            {THEME_PRESETS.map(({ key, label, Icon, color, desc }) => (
              <ListItemButton
                key={key}
                selected={themePreset === key}
                onClick={() => handleThemePresetChange(key)}
                sx={{
                  mx: 1,
                  mb: 0.5,
                  borderRadius: 2,
                  '&.Mui-selected': {
                    backgroundColor: alpha(color, 0.18),
                    border: `1px solid ${alpha(color, 0.4)}`,
                    '&:hover': { backgroundColor: alpha(color, 0.22) },
                  },
                  '&:hover': {
                    backgroundColor: isDark ? alpha('#fff', 0.06) : alpha('#000', 0.04),
                  },
                }}
              >
                <ListItemIcon sx={{ minWidth: 40 }}>
                  <Icon sx={{ fontSize: 22, color: themePreset === key ? color : secondaryColor }} />
                </ListItemIcon>
                <ListItemText
                  primary={label}
                  secondary={desc}
                  primaryTypographyProps={{ fontWeight: themePreset === key ? 600 : 500, color: themePreset === key ? color : textColor }}
                  secondaryTypographyProps={{ variant: 'caption', color: secondaryColor }}
                />
              </ListItemButton>
            ))}
          </List>
          {presetSuccess && (
            <Typography variant="caption" sx={{ color: THEME_COLOR, display: 'block', px: 2, pb: 1.5 }}>
              Theme updated. See preview →
            </Typography>
          )}
          <Box sx={{ p: 2, pt: 0 }}>
            <Button
              component="a"
              href={previewUrl}
              target="_blank"
              rel="noopener noreferrer"
              size="small"
              startIcon={<OpenInNewIcon />}
              sx={{
                color: THEME_COLOR,
                '&:hover': { backgroundColor: alpha(THEME_COLOR, 0.1) },
              }}
            >
              Open in new tab
            </Button>
          </Box>
          <Box sx={{ borderTop: `1px solid ${borderColor}`, p: 2, pt: 2 }}>
            <Typography variant="caption" sx={{ color: secondaryColor, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 0.5, mb: 1.5 }}>
              <ImageIcon sx={{ fontSize: 16 }} />
              Upload logo
            </Typography>
            {logoUrl ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 1 }}>
                <Box
                  component="img"
                  src={logoUrl}
                  alt="NexaStay logo"
                  sx={{ maxHeight: 56, maxWidth: '100%', objectFit: 'contain', borderRadius: 1, border: `1px solid ${borderColor}` }}
                />
                <input
                  ref={logoInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/gif,image/webp"
                  onChange={handleLogoUpload}
                  style={{ display: 'none' }}
                />
                <Button
                  size="small"
                  disabled={uploadingLogo}
                  startIcon={uploadingLogo ? <CircularProgress size={16} sx={{ color: 'inherit' }} /> : <CloudUploadIcon />}
                  onClick={() => logoInputRef.current?.click()}
                  sx={{ color: THEME_COLOR, '&:hover': { backgroundColor: alpha(THEME_COLOR, 0.1) } }}
                >
                  {uploadingLogo ? 'Uploading…' : 'Change logo'}
                </Button>
              </Box>
            ) : (
              <>
                <input
                  ref={logoInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/gif,image/webp"
                  onChange={handleLogoUpload}
                  style={{ display: 'none' }}
                />
                <Button
                  size="small"
                  variant="outlined"
                  disabled={uploadingLogo}
                  startIcon={uploadingLogo ? <CircularProgress size={16} sx={{ color: 'inherit' }} /> : <CloudUploadIcon />}
                  onClick={() => logoInputRef.current?.click()}
                  sx={{
                    borderColor: borderColor,
                    color: textColor,
                    '&:hover': { borderColor: THEME_COLOR, backgroundColor: alpha(THEME_COLOR, 0.08) },
                  }}
                >
                  {uploadingLogo ? 'Uploading…' : 'Upload logo'}
                </Button>
                <Typography variant="caption" sx={{ color: secondaryColor, display: 'block', mt: 0.5 }}>
                  Same folder as property images, saved as logo
                </Typography>
              </>
            )}
          </Box>
        </Paper>

        {/* Center: mobile preview */}
        <Box
          sx={{
            width: { xs: '100%', lg: 'auto' },
            flex: { xs: '1 1 auto', lg: '0 0 auto' },
            flexShrink: 0,
            display: 'flex',
            justifyContent: { xs: 'stretch', lg: 'center' },
            minWidth: 0,
          }}
        >
          <NexaStayMobilePreview
            previewUrl={previewUrl}
            previewRefreshKey={previewRefreshKey}
            isDark={isDark}
            borderColor={borderColor}
            secondaryColor={secondaryColor}
            isCompact={isCompact}
          />
        </Box>

        {/* Right: Page analytics */}
        <Box sx={{ flex: 1, minWidth: { xs: 0, lg: 320 }, width: '100%' }}>
          <Paper
            elevation={0}
            sx={{
              height: '100%',
              borderRadius: 3,
              backgroundColor: cardBg,
              border: `1px solid ${borderColor}`,
              overflow: 'hidden',
              p: 2,
            }}
          >
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1.5, mb: 2 }}>
            <Box>
              <Typography variant="subtitle1" sx={{ color: textColor, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1 }}>
                <TrendingUpIcon sx={{ color: THEME_COLOR, fontSize: 22 }} />
                Page analytics
              </Typography>
              <Typography variant="caption" sx={{ color: secondaryColor, display: 'block', mt: 0.25 }}>
                Unique visitors by IP from your booking engine
              </Typography>
            </Box>
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel id="analytics-period-label" sx={{ color: secondaryColor }}>Period</InputLabel>
              <Select
                labelId="analytics-period-label"
                value={analyticsPeriod}
                label="Period"
                onChange={(e) => setAnalyticsPeriod(e.target.value)}
                sx={{
                  color: textColor,
                  borderRadius: 2,
                  '& .MuiOutlinedInput-notchedOutline': { borderColor: borderColor },
                  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: THEME_COLOR },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: THEME_COLOR },
                }}
              >
                <MenuItem value="today">Today</MenuItem>
                <MenuItem value="yesterday">Yesterday</MenuItem>
                <MenuItem value="this_month">This month</MenuItem>
                <MenuItem value="last_month">Last month</MenuItem>
                <MenuItem value="custom">Custom date range</MenuItem>
              </Select>
            </FormControl>
          </Box>
          {analyticsPeriod === 'custom' && (
            <LocalizationProvider dateAdapter={AdapterMoment}>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
                <DatePicker
                  label="Start date"
                  value={customDateStart}
                  onChange={setCustomDateStart}
                  slotProps={{ textField: { size: 'small', sx: { '& .MuiOutlinedInput-root': { borderRadius: 2 } } } }}
                />
                <DatePicker
                  label="End date"
                  value={customDateEnd}
                  onChange={setCustomDateEnd}
                  minDate={customDateStart}
                  slotProps={{ textField: { size: 'small', sx: { '& .MuiOutlinedInput-root': { borderRadius: 2 } } } }}
                />
              </Box>
            </LocalizationProvider>
          )}
          {analyticsError && (
            <Typography variant="caption" sx={{ color: 'error.main', display: 'block', mb: 1 }}>{analyticsError}</Typography>
          )}
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, p: 1.5, borderRadius: 2, bgcolor: alpha(THEME_COLOR, 0.12), border: `1px solid ${alpha(THEME_COLOR, 0.3)}` }}>
              <PeopleIcon sx={{ color: THEME_COLOR, fontSize: 28 }} />
              <Box>
                <Typography variant="caption" sx={{ color: secondaryColor }}>Visitors ({analyticsPeriod === 'today' ? 'today' : analyticsPeriod === 'yesterday' ? 'yesterday' : analyticsPeriod === 'this_month' ? 'this month' : analyticsPeriod === 'last_month' ? 'last month' : 'selected range'})</Typography>
                {analyticsLoading && !analyticsData ? (
                  <CircularProgress size={24} sx={{ color: THEME_COLOR, mt: 0.5 }} />
                ) : (
                  <Typography variant="h5" sx={{ fontWeight: 700, color: textColor }}>{analytics.totalVisitors.toLocaleString()}</Typography>
                )}
              </Box>
            </Box>
            <Box sx={{ width: '100%', minHeight: 200 }}>
              <Typography variant="caption" sx={{ color: secondaryColor, fontWeight: 600, mb: 1, display: 'block' }}>Visitors over time</Typography>
              {analyticsLoading && !analytics.chartData?.length ? (
                <Box sx={{ height: 180, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <CircularProgress size={32} sx={{ color: THEME_COLOR }} />
                </Box>
              ) : !(analytics.chartData && analytics.chartData.length > 0) ? (
                <Box sx={{ height: 180, display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: 2, bgcolor: isDark ? alpha('#fff', 0.04) : alpha('#000', 0.04) }}>
                  <Typography variant="body2" sx={{ color: secondaryColor, textAlign: 'center', px: 2 }}>
                    No visits in this period. Visitors are recorded when someone opens your booking page.
                  </Typography>
                </Box>
              ) : (
              <ResponsiveContainer width="100%" height={180}>
                <AreaChart data={analytics.chartData} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                  <defs>
                    <linearGradient id="analyticsAreaGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor={THEME_COLOR} stopOpacity={0.4} />
                      <stop offset="100%" stopColor={THEME_COLOR} stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke={isDark ? alpha('#fff', 0.08) : alpha('#000', 0.08)} vertical={false} />
                  <XAxis dataKey="name" tick={{ fontSize: 10, fill: secondaryColor }} axisLine={{ stroke: borderColor }} tickLine={false} />
                  <YAxis tick={{ fontSize: 10, fill: secondaryColor }} axisLine={false} tickLine={false} width={28} />
                  <RechartsTooltip
                    contentStyle={{ borderRadius: 8, border: `1px solid ${borderColor}` }}
                    labelStyle={{ color: textColor }}
                    formatter={(value) => [value, 'Visitors']}
                    labelFormatter={(label) => `Period: ${label}`}
                  />
                  <Area type="monotone" dataKey="visitors" stroke={THEME_COLOR} strokeWidth={2} fill="url(#analyticsAreaGradient)" />
                </AreaChart>
              </ResponsiveContainer>
              )}
            </Box>
            <Box sx={{ width: '100%' }}>
              <Typography variant="caption" sx={{ color: secondaryColor, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
                <PublicIcon sx={{ fontSize: 14 }} /> Visitors by country
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                {(analytics.byCountry && analytics.byCountry.length > 0) ? (
                  analytics.byCountry.map(({ country, count, pct }) => (
                    <Box key={country || count} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="caption" sx={{ color: textColor, minWidth: 100 }}>{country || '—'}</Typography>
                      <Box sx={{ flex: 1, height: 8, borderRadius: 1, bgcolor: isDark ? alpha('#fff', 0.08) : alpha('#000', 0.06) }}>
                        <Box sx={{ width: `${Number(pct) || 0}%`, height: '100%', borderRadius: 1, bgcolor: THEME_COLOR, transition: 'width 0.3s' }} />
                      </Box>
                      <Typography variant="caption" sx={{ color: secondaryColor }}>{count != null ? count : 0}</Typography>
                    </Box>
                  ))
                ) : (
                  <Typography variant="caption" sx={{ color: secondaryColor }}>No country data yet</Typography>
                )}
              </Box>
            </Box>
          </Box>
        </Paper>
        </Box>
      </Box>

      {/* Row 2: Full-width live preview – desktop only (mobile uses full-width preview above) */}
      {!isCompact ? (
      <Paper
        elevation={0}
        sx={{
          width: '100%',
          borderRadius: 3,
          backgroundColor: isDark ? alpha('#000', 0.2) : alpha('#000', 0.04),
          border: `1px solid ${borderColor}`,
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: 1,
            px: 2,
            py: 1.5,
            borderBottom: `1px solid ${borderColor}`,
            backgroundColor: isDark ? alpha('#fff', 0.04) : alpha('#000', 0.02),
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <LaptopIcon sx={{ fontSize: 20, color: secondaryColor }} />
            <Typography variant="subtitle2" sx={{ color: secondaryColor, fontWeight: 600 }}>
              Live preview
            </Typography>
            <Typography variant="caption" sx={{ color: secondaryColor, opacity: 0.8 }}>
              Changes reflect here when you save
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Button
              component="a"
              href={previewUrl}
              target="_blank"
              rel="noopener noreferrer"
              size="small"
              startIcon={<OpenInNewIcon />}
              sx={{ color: secondaryColor, fontSize: '0.75rem' }}
            >
              Open in new tab
            </Button>
            <Tooltip title="Refresh preview">
              <IconButton size="small" onClick={refreshPreview} sx={{ color: secondaryColor }}>
                <RefreshIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
        <Box
          sx={{
            width: '100%',
            minHeight: 520,
            position: 'relative',
            backgroundColor: isDark ? '#1a1a1a' : '#e8e8e8',
          }}
        >
          <Box
            component="iframe"
            ref={iframeRef}
            title="NexaStay preview"
            src={buildPreviewIframeSrc(previewUrl, previewRefreshKey)}
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              border: 0,
            }}
          />
        </Box>
      </Paper>
      ) : null}
    </Box>
  );
};

export default NexaStaySection;
